'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';

import { SecondaryHeading } from '@/components/typography';
import { ItemsTextForIcons } from '@/lib/marketing-page/dto';
import AvIcon from '@/public/icons/amenityIcon-av-equi.svg';
import CateringIcon from '@/public/icons/amenityIcon-catering.svg';
import CoffeeIcon from '@/public/icons/amenityIcon-coffee.svg';
import ConferenceIcon from '@/public/icons/amenityIcon-conference.svg';
import eventsIcon from '@/public/icons/amenityIcon-events.svg';
import MailIcon from '@/public/icons/amenityIcon-mail.svg';
import OnSiteIcon from '@/public/icons/amenityIcon-onsite.svg';
import PeopleMeetingIcon from '@/public/icons/amenityIcon-people-meeting.svg';
import PhoneIcon from '@/public/icons/amenityIcon-phone.svg';
import PresentationIcon from '@/public/icons/amenityIcon-presentation.svg';
import PrintingIcon from '@/public/icons/amenityIcon-printer.svg';
import CleaningIcon from '@/public/icons/amenityIcon-professional-cleaning.svg';
import ReceptionIcon from '@/public/icons/amenityIcon-reception.svg';
import SeatingIcon from '@/public/icons/amenityIcon-seating.svg';
import WifiIcon from '@/public/icons/amenityIcon-wifi.svg';
import { getBackgroundSectionClassName } from '@/utils/marketing-landing-page';

import { BackgroundColorProps } from '../types';

import clsx from 'clsx';

const NB_ITEMS_TO_SHOW_MOBILE = 5;

const ICONS_MAP: Record<string, string> = {
  [ItemsTextForIcons.HIGH_SPEED_WIFI]: WifiIcon,
  [ItemsTextForIcons.FAST_SECURE_WiFi]: WifiIcon,
  [ItemsTextForIcons.HIGH_SPEED_INTERNET]: WifiIcon,
  [ItemsTextForIcons.CONFERENCE_PHONE_SYSTEMS]: ConferenceIcon,
  [ItemsTextForIcons.PRIVATE_CONFERENCE_ROOMS]: ConferenceIcon,
  [ItemsTextForIcons.ON_DEMAND_MEETING_ROOMS]: ConferenceIcon,
  [ItemsTextForIcons.PROFESSIONAL_RECEPTION_SERVICES]: ReceptionIcon,
  [ItemsTextForIcons.VIRTUAL_RECEPTIONIST_SERVICES]: ReceptionIcon,
  [ItemsTextForIcons.AV_EQUIPMENT]: AvIcon,
  [ItemsTextForIcons.COMFORTABLE_SEATING]: SeatingIcon,
  [ItemsTextForIcons.PRINTING_OFFICE_SUPPLIES]: PrintingIcon,
  [ItemsTextForIcons.UNLIMITED_COLOR_PRINTING]: PrintingIcon,
  [ItemsTextForIcons.WHITEBOARDS_PRESENTATION_TOOLS]: PresentationIcon,
  [ItemsTextForIcons.COMPLIMENTARY_DRINKS]: CoffeeIcon,
  [ItemsTextForIcons.DAILY_BREAKFAST_CRAFT_COFFEE]: CoffeeIcon,
  [ItemsTextForIcons.ON_SITE_MANAGER]: OnSiteIcon,
  [ItemsTextForIcons.PRESTIGIOUS_BUSINESS_ADDRESS]: PeopleMeetingIcon,
  [ItemsTextForIcons.PROGRAMMING_EVENTS]: eventsIcon,
  [ItemsTextForIcons.ACCESS_TO_NETWORKING_EVENTS]: eventsIcon,
  [ItemsTextForIcons.PHONE_BOOTHS]: PhoneIcon,
  [ItemsTextForIcons.OFFICE_SUPPLIES]: CateringIcon,
  [ItemsTextForIcons.PROFESSIONAL_GRADE_CLEANINGS]: CleaningIcon,
  [ItemsTextForIcons.MAIL_PACKAGING_SERVICES]: MailIcon,
  [ItemsTextForIcons.PROFESSIONAL_MAIL_HANDLING]: MailIcon,
};

type ItemListModuleProps = {
  title?: string;
  items: string[];
  background?: BackgroundColorProps;
  showIcons?: boolean;
};

export default function ItemListModule({
  title,
  items,
  background,
  showIcons,
}: ItemListModuleProps) {
  const t = useTranslations('general');
  const [isReadMore, setIsReadMore] = useState(false);

  const handleReadMoreClicked = () => {
    setIsReadMore((prevValue) => !prevValue);
  };

  return (
    <section
      className={clsx(
        background?.color && getBackgroundSectionClassName(background?.color),
        'flex'
      )}
    >
      <div className="my-10 medium-container md:my-14 max-lg:w-full">
        <div className="flex flex-col max-w-[1058px] lg:w-[1058px] w-full ">
          <SecondaryHeading className="max-sm:text-[1.5rem]">
            {title}
          </SecondaryHeading>

          <ul
            className={clsx(
              'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 md:grid-rows-8 lg:grid-rows-5',
              'mt-4 md:mt-6',
              'gap-y-2 md:gap-y-4'
            )}
          >
            {items.map((item, index) => {
              const iconSrc = ICONS_MAP[item];
              return (
                <li
                  key={item}
                  className={clsx(
                    'flex gap-2',
                    index > NB_ITEMS_TO_SHOW_MOBILE - 1 &&
                      !isReadMore &&
                      'max-md:hidden self-center text-center'
                  )}
                >
                  {showIcons && iconSrc && (
                    <Image src={iconSrc} alt={item} aria-hidden="true" />
                  )}
                  <p
                    className={'self-center text-center font-normal text-base'}
                  >
                    {item}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>

        {items && items.length >= NB_ITEMS_TO_SHOW_MOBILE && (
          <button
            className="self-start mt-2 underline cursor-pointer md:hidden text-teal-40"
            onClick={handleReadMoreClicked}
          >
            {isReadMore ? t('read-less') : t('read-more')}
          </button>
        )}
      </div>
    </section>
  );
}
